import styled from 'styled-components';

import {media} from '../../styles/components/media-queries';
import {ToolTips} from '../../styles/components/toolTips';
import {CARD_MIN_HEIGHT, Container} from './ListProduct.style';
import {AddButton, OfferWrapper} from './Product.style';

/*
 * Presentation for the peer-gap trial cards in the search grid.
 *
 * Everything the trial adds to a product card lives in this file and FeaturedProduct.js: the card
 * itself is an ordinary <Product>, wrapped rather than modified. Removing the trial is deleting
 * these two files, the FEATURED_ITEM_* constants and the branch in SearchPage.js — no shared
 * product component carries an isFeatured prop.
 *
 * The card is marked once, at the top edge, and left alone everywhere else: the label stands in a
 * filled strip in exactly the place, and of exactly the size, that an offer card's "Mix & Save"
 * strip occupies. A featured cell therefore has the same silhouette as every other cell in the
 * row, and differs from an offer cell only in the colour and wording of that strip.
 *
 * Recolouring the card's interior was tried and dropped. It went through a scoped theme override
 * on primaryColor/tertiaryColor, which reaches further than it appears to: those two also carry
 * the price ladder — base price grey, "any 10+" blue, "any 20+" red — so a featured card rendered
 * all four of its tiered prices in one purple, losing the distinction between the tiers, and the
 * product name lost the blue that marks it as the link to the product. tertiaryBgColor tinted the
 * card surface as well, which dropped the contrast of the grey price labels and put the cut-out
 * product photography on a colour it is not shot for.
 *
 * The strip itself is purple (theme.featuredColor) deliberately: these cards are recommendations,
 * not promotions. Reusing the offer red would tell customers a non-offer product is discounted,
 * and would make the two indistinguishable when reading the trial's results.
 */

// The strip stands where the offer strip stands, so it is that strip's height (OfferWrapper's own
// 3rem min-height). Load-bearing in both directions — the card gives up exactly this much to make
// room for it — so the two are one number.
export const FEATURED_LABEL_HEIGHT = '3rem';

/*
 * A featured cell must occupy exactly the same box as an ordinary one — the grid's rows are
 * auto-sized, so one taller cell stretches its whole row and leaves every other card in it
 * standing above a band of empty background.
 *
 * The strip is therefore paid for out of the card, not added on top of it: the card gives up the
 * 3rem of its own offer strip, which is hidden below, and gives up its top margin to sit flush
 * beneath the replacement.
 *
 *   margin 0.8 + strip 3 + card 23.5 + margin 0.8 = 28.1rem = an ordinary cell's 0.8 + 26.5 + 0.8
 *
 * That 3rem is the only height the card has to give, and hiding OfferWrapper is what gives it:
 * the card sizes itself from its content now, so the hidden strip takes its own 3rem out of the
 * card's natural height and the floor below simply follows it down. Taking height from anywhere
 * else collides the prices with the Add button.
 *
 * Below `smaller` the card drops its floor entirely (Container's own `min-height: auto`) and the
 * grid is one column, so there is no row to match and nothing to subtract from.
 *
 * align-self: end anchors the whole wrapper to the foot of its row, so the card's bottom edge
 * lines up with the full-height cards beside it however the row turns out.
 */
export const FeaturedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-self: end;
  min-width: 0;
  max-width: 100%;

  ${Container} {
    min-height: calc(${CARD_MIN_HEIGHT} - ${FEATURED_LABEL_HEIGHT});
    /* The strip has taken this margin, so the card sits directly under it. */
    margin-top: 0;
    ${media.smaller`
      min-height: auto;
    `};
    /* Ties the card to its strip. Drawn inside the box so it cannot widen the cell, and kept to a
       hairline: the strip is the signal, and a heavier frame competes with it. */
    box-shadow: inset 0 0 0 0.1rem ${p => p.theme.featuredColor};
  }

  /*
   * The card's own strip goes entirely: the label has taken its row, and its contents are all
   * things a featured card does not need. The offer text is one of them — these cards are
   * recommendations first, and the tiered prices below carry the offer on their own. The favourite
   * and previously-purchased icons are the others: a peer-gap product is by definition one this
   * customer has never bought, so neither has anything to say about it.
   */
  ${OfferWrapper} {
    display: none;
  }

  /* Carries the strip's colour onto the card's own call to action, so the accent isn't confined
     to a band the customer may not even associate with the button below it. */
  ${AddButton} {
    /* Read by Button3Inverted's own loading background-color (see buttons.js) — a CSS custom
       property is what lets this reach it, since $isLoading is a transient prop Product.js sets
       on AddButton itself and is invisible from here. */
    --button3-inverted-loading-bg: ${p => p.theme.featuredColor};
    border-color: ${p => p.theme.featuredColor};
    color: ${p => p.theme.featuredColor};

    &:hover {
      background-color: ${p => p.theme.featuredColor};
      border-color: ${p => p.theme.featuredColor};
      color: ${p => p.theme.primaryTextColor};
    }
  }
`;

/*
 * Mirrors Container's own box — the same fixed width and the same side margins — so the strip and
 * the card share an edge on all three sides rather than merely appearing to.
 *
 * Fixed at exactly the height the card gave up. Left to size itself, a label that wrapped to a
 * second line would make its cell taller than the rest of its row; the label truncates instead.
 */
export const FeaturedHeader = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  width: 35rem;
  max-width: 35rem;
  height: ${FEATURED_LABEL_HEIGHT};
  margin: 0.8rem 0 0;
  background-color: ${p => p.theme.featuredColor};
  ${media.smaller`
    width: 100%;
    max-width: 100%;
    margin: 0;
  `};
`;

/* Typed as OfferText is, so the two strips are the same strip in two colours. */
export const FeaturedLabel = styled.span`
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
  padding-left: 2rem;
  /* Keeps the ellipsis off the info button beside it. */
  padding-right: 1rem;
  color: ${p => p.theme.primaryTextColor};
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  ${media.medium`
    font-size: 1.1rem;
    padding-left: 1rem;
  `};
`;

/*
 * Sits where the favourite icon sits on every other card — TopButtonGroup's own 1rem corner —
 * but reaches it as a flex item pushed right, not by being positioned.
 *
 * This wrapper must not open a stacking context, which rules out both of the obvious ways to put
 * it in the corner: `position: absolute` with a `transform` to centre it, and `position: absolute`
 * with a `z-index` to lift it. The tooltip below is `position: absolute; z-index: 99` — high
 * enough to clear the card, which follows it in the DOM and is itself positioned — and a z-index
 * only competes within its own stacking context. Open one here and the tooltip's 99 is trapped
 * inside it, the whole wrapper paints in DOM order at the header's level, and the tooltip
 * disappears behind the card: still in the tree, still `display: block`, simply painted under.
 */
export const FeaturedInfoWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin-left: auto;
  margin-right: 1rem;
  ${media.small`
    margin-right: 0.6rem;
  `};
`;

export const FeaturedInfoButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 1.8rem;
  height: 1.8rem;
  padding: 0;
  border: 0.1rem solid ${p => p.theme.primaryTextColor};
  border-radius: 50%;
  background-color: transparent;
  color: ${p => p.theme.primaryTextColor};
  font-family: serif;
  font-style: italic;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease-out, color 0.15s ease-out;

  /*
   * The circle is 1.8rem, far below a comfortable tap target, and it cannot grow without
   * unbalancing the label beside it. This widens the tappable area to 4.4rem without moving the
   * circle. Absolutely positioned, so it is not a flex item and leaves the glyph centred.
   *
   * The offsets are measured from the button's padding box, so they allow for the 0.1rem border on
   * each side. Vertically it fills the strip and stops there — 1.6 + 0.7 + 0.7 = 3rem, the strip
   * exactly — because the product link starts immediately below and a taller target would swallow
   * taps meant for it. Horizontally it is deliberately lopsided: the button sits 1rem from the
   * card's right edge, and 0.1 + 0.9 puts the target's edge on the card's, so it never reaches
   * across the narrow gap into the neighbouring cell. The width lost on the right is taken on the
   * left instead: 1.6 + 0.9 + 1.9 = 4.4rem.
   */
  &::before {
    content: '';
    position: absolute;
    top: -0.7rem;
    right: -0.9rem;
    bottom: -0.7rem;
    left: -1.9rem;
  }

  &:hover {
    background-color: ${p => p.theme.primaryTextColor};
    color: ${p => p.theme.featuredColor};
  }

  /* The browser default ring is tuned for neither this colour nor this strip. */
  &:focus-visible {
    outline: 0.2rem solid ${p => p.theme.primaryTextColor};
    outline-offset: 0.2rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/*
 * Anchored to the icon's right edge and opening leftwards, so it stays inside the card's 35rem
 * column. Opening rightwards put it past the edge of the grid for the last column in the row,
 * where .infinite-scroll-component's overflow: hidden clipped it.
 */
export const FeaturedToolTip = styled(ToolTips)`
  left: auto;
  right: -0.8rem;
  top: 2.6rem;
  width: 24rem;
  text-align: left;
  text-transform: none;
  font-weight: normal;

  &::before {
    left: auto;
    right: 1rem;
    top: -0.7rem;
  }

  ${media.smaller`
    width: 22rem;
  `};
`;

export const FeaturedToolTipReason = styled.div`
  padding-top: 0.4rem;
  font-weight: bold;
`;
